import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { queryKeys } from '@core/api/queryKeys';
import { buildQueryString } from '@shared/lib/buildQueryString';

/**
 * Response of `GET /api/v1/stats/first-trade`. `firstTradeAt` is the earliest `executed_at` across the
 * user's trades (account-scoped when an `account` is passed) as an ISO-8601 string, or `null` when the
 * account has no trades.
 */
export interface FirstTrade {
  firstTradeAt: string | null;
}

/**
 * The account's first-trade date. Scoped by **account only** (NOT the date range). Used to (a) seed the
 * global date range's START to the first trade (see `useSeedDateRangeFromFirstTrade`) and (b) drive the
 * date-picker's disabled state — `firstTradeAt === null` (on success) means the account has no trades.
 * Re-fetched on `trades.changed` via `useRealtimeInvalidation(['first-trade'])` so a first import unlocks it.
 */
export function useFirstTrade(params: { account?: string } = {}) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.firstTrade(params),
    queryFn:  () => api.get<FirstTrade>(`/api/v1/stats/first-trade${buildQueryString(params)}`),
  });
}
